import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

type DebounceOptions = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
};

type ControlFunctions = {
  cancel: () => void;
  flush: () => void;
  isPending: () => boolean;
};

export type DebouncedState<TArgs extends unknown[], TReturn> = ((
  ...args: TArgs
) => TReturn | undefined) &
  ControlFunctions;

export function useDebounceCallback<TArgs extends unknown[], TReturn>(
  func: (...args: TArgs) => TReturn,
  delay = 500,
  options?: DebounceOptions,
): DebouncedState<TArgs, TReturn> {
  const debouncedFunc = useRef<ReturnType<typeof debounce>>();

  useEffect(() => {
    return () => {
      if (debouncedFunc.current) {
        debouncedFunc.current.cancel();
      }
    };
  }, []);

  const debounced = useMemo(() => {
    const debouncedFuncInstance = debounce(func, delay, options);

    const wrappedFunc: DebouncedState<TArgs, TReturn> = (...args: TArgs) => {
      return debouncedFuncInstance(...args);
    };

    wrappedFunc.cancel = () => {
      debouncedFuncInstance.cancel();
    };

    wrappedFunc.isPending = () => {
      return !!debouncedFunc.current;
    };

    wrappedFunc.flush = () => {
      return debouncedFuncInstance.flush();
    };

    return wrappedFunc;
  }, [func, delay, options]);

  // Update the debounced function ref whenever func, wait, or options change
  useEffect(() => {
    debouncedFunc.current = debounce(func, delay, options);
  }, [func, delay, options]);

  return debounced;
}
